/**
 * The ONE HTTP client. Every API module goes through `request()` — auth
 * header, JSON handling, error normalization, and the 401→refresh→retry
 * dance live here and nowhere else.
 *
 * Token storage: localStorage (all tabs share the session — and share the
 * SAME long-lived refresh token; the backend deliberately does not rotate
 * refresh tokens because rotation raced across tabs on the source
 * platform and force-logged-out everything).
 */

// Same-origin relative path by default — in production Front Door serves the
// SPA and the API under one host, so `/api/v1` just works and stays
// domain-agnostic. Override with VITE_API_BASE_URL (absolute, INCLUDING the
// /api/v1 suffix) when the SPA and API are on different origins, e.g. staging
// (storage static site + a separate Container App); CORS must then allow the
// SPA origin.
export const API_BASE = import.meta.env.VITE_API_BASE_URL || "/api/v1";

const ACCESS_KEY = "discentem.access";
const REFRESH_KEY = "discentem.refresh";

export class ApiError extends Error {
  status: number;
  fieldErrors: Record<string, string>;
  /** Set on a 403 the content gate raised: the module the tenant needs to
   *  own (e.g. "content.a2.practice"). Lets the UI say "you don't have A2
   *  practice yet — get it in the Store" instead of a generic failure. */
  requiredModule: string | null;

  constructor(
    status: number,
    detail: string,
    fieldErrors?: Record<string, string>,
    requiredModule?: string | null,
  ) {
    super(detail);
    this.status = status;
    this.fieldErrors = fieldErrors ?? {};
    this.requiredModule = requiredModule ?? null;
  }
}

export function getTokens(): { access: string | null; refresh: string | null } {
  return {
    access: localStorage.getItem(ACCESS_KEY),
    refresh: localStorage.getItem(REFRESH_KEY),
  };
}

export function setTokens(access: string, refresh: string): void {
  localStorage.setItem(ACCESS_KEY, access);
  localStorage.setItem(REFRESH_KEY, refresh);
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

/** Session-expired listeners (AuthContext subscribes to route to /login). */
type Listener = () => void;
const sessionExpiredListeners = new Set<Listener>();

export function onSessionExpired(fn: Listener): () => void {
  sessionExpiredListeners.add(fn);
  return () => sessionExpiredListeners.delete(fn);
}

function emitSessionExpired(): void {
  clearTokens();
  for (const fn of sessionExpiredListeners) fn();
}

async function parseError(res: Response): Promise<ApiError> {
  let detail = `HTTP ${res.status}`;
  let fieldErrors: Record<string, string> | undefined;
  try {
    const body = await res.json();
    if (typeof body?.detail === "string") detail = body.detail;
    if (body?.field_errors && typeof body.field_errors === "object") {
      fieldErrors = body.field_errors;
    }
  } catch {
    // Non-JSON error body — keep the HTTP status message.
  }
  return new ApiError(
    res.status, detail, fieldErrors, res.headers.get("X-Required-Module"),
  );
}

/** One in-flight refresh shared by all concurrent 401s (no stampede). */
let refreshInFlight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const { refresh } = getTokens();
      if (!refresh) return false;
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refresh }),
      });
      if (!res.ok) return false;
      const body = await res.json();
      setTokens(body.access_token, body.refresh_token);
      return true;
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** Skip the Authorization header + refresh dance (public endpoints). */
  anonymous?: boolean;
  /** Query params; null/undefined entries are dropped. */
  params?: Record<string, string | number | boolean | null | undefined>;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, anonymous = false, params } = options;

  let url = `${API_BASE}${path}`;
  if (params) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== null && v !== undefined) qs.set(k, String(v));
    }
    const encoded = qs.toString();
    if (encoded) url += `?${encoded}`;
  }

  const doFetch = () => {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (!anonymous) {
      const { access } = getTokens();
      if (access) headers["Authorization"] = `Bearer ${access}`;
    }
    return fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  };

  let res = await doFetch();

  // One refresh attempt per request; a second 401 means the session is over.
  if (res.status === 401 && !anonymous) {
    const refreshed = await tryRefresh();
    if (!refreshed) {
      emitSessionExpired();
      throw await parseError(res);
    }
    res = await doFetch();
    if (res.status === 401) {
      emitSessionExpired();
      throw await parseError(res);
    }
  }

  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
